import { useTable } from "@refinedev/core";
import { Link } from "react-router";

import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
} from "@/components/Page";
import { EmptyRow, Sub, Table, Td, Th, Tr } from "@/components/Table";
import { formatDateTime } from "@/lib/format";
import { filterValue } from "@/lib/query";

interface FamilyRow extends Record<string, unknown> {
  id: string;
  name: string;
  owner_name: string | null;
  owner_email: string | null;
  members_count: number;
  memoirs_count: number;
  created_at: string;
}

export function FamiliesListPage() {
  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<FamilyRow>({
      resource: "families",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    });

  return (
    <>
      <PageHeader
        title="Rodziny"
        description="Kto z kim i ile Opowieści prowadzi. Stąd wchodzi się w Opowieści konkretnej rodziny."
      />

      <FilterBar>
        <SearchInput
          label="Szukaj"
          placeholder="nazwa rodziny, właściciel"
          value={filterValue(filters, "query")}
          onChange={(value) => setFilters([{ field: "query", operator: "eq", value }], "merge")}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Rodzina</Th>
                <Th>Właściciel</Th>
                <Th align="right">Członkowie</Th>
                <Th align="right">Opowieści</Th>
                <Th>Założona</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={5}>Żadna rodzina nie pasuje do tych filtrów.</EmptyRow>
              )}
              {result.data.map((family) => (
                <Tr key={family.id}>
                  <Td>
                    <Link
                      to={`/opowiesci?family_id=${family.id}`}
                      className="text-accent underline underline-offset-4 hover:text-accent-dark"
                    >
                      {family.name}
                    </Link>
                  </Td>
                  <Td>
                    {family.owner_name ?? "-"}
                    {family.owner_email && <Sub>{family.owner_email}</Sub>}
                  </Td>
                  <Td align="right" numeric>
                    {family.members_count}
                  </Td>
                  <Td align="right" numeric>
                    {family.memoirs_count}
                  </Td>
                  <Td nowrap>{formatDateTime(family.created_at)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
